'use client'

import { useEffect, useState } from 'react'

/**
 * R69 + Politica 04 — Banner de consentimento de cookies.
 *
 * Categorias conforme parecer juridico:
 *   - estritamente necessarios (sempre on, dispensam consentimento);
 *   - funcionais, analiticos, terceiros (opt-in).
 *
 * Persiste em localStorage para evitar reapresentacao e envia ao backend
 * (POST /legal/cookie-consent) o registro tecnico do consentimento.
 */

const POLICY_VERSION = '1.0'
const STORAGE_KEY = 'imobiliar.cookieConsent.v1'

interface ConsentChoice {
  functional: boolean
  analytics: boolean
  thirdParty: boolean
  policyVersion: string
  decidedAt: string
}

function loadStored(): ConsentChoice | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ConsentChoice
    if (parsed.policyVersion !== POLICY_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

function getOrCreateAnonId(): string {
  if (typeof window === 'undefined') return ''
  const KEY = 'imobiliar.anonVisitorId'
  let id = window.localStorage.getItem(KEY)
  if (!id) {
    id = crypto.randomUUID()
    window.localStorage.setItem(KEY, id)
  }
  return id
}

async function postConsent(payload: Omit<ConsentChoice, 'decidedAt'> & { anonVisitorId: string }) {
  const baseUrl = process.env.NEXT_PUBLIC_API_URL
  if (!baseUrl) return
  await fetch(`${baseUrl}/legal/cookie-consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include',
  }).catch(() => undefined)
}

export function CookieBanner() {
  const [open, setOpen] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [functional, setFunctional] = useState(true)
  const [analytics, setAnalytics] = useState(false)
  const [thirdParty, setThirdParty] = useState(false)

  useEffect(() => {
    const stored = loadStored()
    if (!stored) setOpen(true)
  }, [])

  const persist = (choice: { functional: boolean; analytics: boolean; thirdParty: boolean }) => {
    const decidedAt = new Date().toISOString()
    const stored: ConsentChoice = { ...choice, policyVersion: POLICY_VERSION, decidedAt }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    }
    void postConsent({ ...choice, policyVersion: POLICY_VERSION, anonVisitorId: getOrCreateAnonId() })
    setOpen(false)
  }

  const acceptAll = () => persist({ functional: true, analytics: true, thirdParty: true })
  const rejectNonEssential = () => persist({ functional: false, analytics: false, thirdParty: false })
  const savePreferences = () => persist({ functional, analytics, thirdParty })

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-label="Preferências de cookies"
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        maxWidth: 520,
        margin: '0 auto',
        padding: 16,
        background: 'var(--surface, #fff)',
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 1000,
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      <p style={{ margin: 0, marginBottom: 12 }}>
        Usamos cookies estritamente necessários ao funcionamento da plataforma. Cookies funcionais,
        analíticos e de terceiros só são ativados com seu consentimento. Veja a{' '}
        <a href="/legal/cookies" style={{ textDecoration: 'underline' }}>
          Política de Cookies
        </a>{' '}
        para detalhes.
      </p>

      {showPreferences && (
        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked disabled aria-label="Estritamente necessários (obrigatórios)" />
            <span>Estritamente necessários (obrigatórios)</span>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={functional}
              onChange={(e) => setFunctional(e.target.checked)}
            />
            <span>Funcionais (preferências de interface, idioma)</span>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
            />
            <span>Analíticos (medição de uso e melhoria do produto)</span>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={thirdParty}
              onChange={(e) => setThirdParty(e.target.checked)}
            />
            <span>Terceiros (integrações de medição/atendimento)</span>
          </label>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {!showPreferences && (
          <button
            type="button"
            onClick={() => setShowPreferences(true)}
            style={btnStyle('ghost')}
          >
            Preferências
          </button>
        )}
        <button type="button" onClick={rejectNonEssential} style={btnStyle('ghost')}>
          Rejeitar não-essenciais
        </button>
        {showPreferences ? (
          <button type="button" onClick={savePreferences} style={btnStyle('primary')}>
            Salvar preferências
          </button>
        ) : (
          <button type="button" onClick={acceptAll} style={btnStyle('primary')}>
            Aceitar todos
          </button>
        )}
      </div>
    </div>
  )
}

function btnStyle(variant: 'primary' | 'ghost'): React.CSSProperties {
  if (variant === 'primary') {
    return {
      padding: '8px 14px',
      borderRadius: 8,
      border: '1px solid var(--accent, #1f6feb)',
      background: 'var(--accent, #1f6feb)',
      color: '#fff',
      cursor: 'pointer',
      fontSize: 13,
    }
  }
  return {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid var(--border, #e5e7eb)',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
  }
}
